import React, { JSX } from 'react';
import { Col, Row } from 'reactstrap';

const Paginator: React.FC<{
  take: number;
  skip: number;
  showing: number;
  totalCount: number;
  setTake: (value: number) => void;
  setSkip: (value: number) => void;
}> = ({ take, skip, showing, totalCount, setTake, setSkip }) => {
  const currentPage = Math.floor(skip / take) + 1;
  const totalPages = Math.ceil(totalCount / take);

  const previousPage = () => {
    setSkip(Math.max(0, skip - take));
  };

  const nextPage = () => {
    setSkip(Math.min(totalCount - take, skip + take));
  };

  const fullPage = () => {
    setTake(totalCount);
    setSkip(0);
  };

  const goToPage = (page: number) => {
    setSkip((page - 1) * take);
  };

  const renderPageNumbers = () => {
    const pageNumbers: JSX.Element[] = [];
    const DOTS = '...';
    const SIBLINGS = 1; // Number of siblings on each side of current page

    // Always show first page
    pageNumbers.push(
      <li key={1} className={`page-item ${1 === currentPage ? 'active' : ''}`}>
        <button className="page-link" onClick={() => goToPage(1)}>
          1
        </button>
      </li>,
    );

    if (currentPage > SIBLINGS + 2) {
      pageNumbers.push(
        <li key="dots-1" className="page-item disabled">
          <span className="page-link">{DOTS}</span>
        </li>,
      );
    }

    // Calculate range around current page
    const startPage = Math.max(2, currentPage - SIBLINGS);
    const endPage = Math.min(totalPages - 1, currentPage + SIBLINGS);

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <li key={i} className={`page-item ${i === currentPage ? 'active' : ''}`}>
          <button className="page-link" onClick={() => goToPage(i)}>
            {i}
          </button>
        </li>,
      );
    }

    if (currentPage < totalPages - (SIBLINGS + 1)) {
      pageNumbers.push(
        <li key="dots-2" className="page-item disabled">
          <span className="page-link">{DOTS}</span>
        </li>,
      );
    }

    // Always show last page if there is more than one page
    if (totalPages > 1) {
      pageNumbers.push(
        <li key={totalPages} className={`page-item ${totalPages === currentPage ? 'active' : ''}`}>
          <button className="page-link" onClick={() => goToPage(totalPages)}>
            {totalPages}
          </button>
        </li>,
      );
    }

    return pageNumbers;
  };

  return (
    <Row className="g-3 text-sm-start my-2 text-center align-middle">
      <Col sm className="mt-0">
        <div className="text-muted d-flex h-100 align-items-center flex-row gap-2">
          <div>
            Showing
            <span className="fw-semibold ms-1">{showing}</span> of <span className="fw-semibold">{totalCount}</span>{' '}
            Results
          </div>

          {totalCount > take && (
            <button className="page-link text-decoration-underline" onClick={fullPage}>
              show all
            </button>
          )}
        </div>
      </Col>
      {totalCount > 0 && (
        <Col sm="auto" className="mt-0">
          <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
            {currentPage > 1 && (
              <li className="page-item">
                <button className="page-link" onClick={previousPage}>
                  Previous
                </button>
              </li>
            )}
            {renderPageNumbers()}
            {currentPage < totalPages && (
              <li className="page-item">
                <button className="page-link" onClick={nextPage}>
                  Next
                </button>
              </li>
            )}
          </ul>
        </Col>
      )}
    </Row>
  );
};

export default Paginator;
